import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import api from "../lib/api";
import { RoleBadge } from "../components/badges";

function UserCard({ user, isRoot }) {
  const rate = user.task_total ? Math.round((100 * user.task_completed) / user.task_total) : 0;
  return (
    <div data-testid={`hierarchy-node-${user.id}`}
      className={`w-60 rounded-xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        isRoot ? "border-primary/30 bg-sidebar text-white" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
          isRoot ? "bg-secondary text-white" : "bg-primary text-white"}`}>
          {user.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
        </span>
        <div className="min-w-0">
          <div className={`truncate font-heading text-sm font-bold ${isRoot ? "text-white" : "text-slate-900"}`}>{user.name}</div>
          <div className={`truncate text-xs ${isRoot ? "text-slate-300" : "text-slate-500"}`}>
            {user.designation || user.department_name}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <RoleBadge role={user.role} />
        <span className={`font-data text-[11px] ${isRoot ? "text-teal-300" : "text-teal-700"}`}>
          {user.task_completed}/{user.task_total} done ({rate}%)
        </span>
      </div>
      <div className={`mt-2 text-[11px] ${isRoot ? "text-slate-400" : "text-slate-500"}`}>
        {user.department_name}
      </div>
    </div>
  );
}

export default function Hierarchy() {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/hierarchy").then((r) => setUsers(r.data.filter((u) => u.status === "active"))).catch(() => {});
  }, []);

  const byId = Object.fromEntries(users.map((u) => [u.id, u]));
  const levels = [...new Set(users.map((u) => u.hierarchy_level))].sort((a, b) => a - b);

  return (
    <div className="animate-fade-in space-y-5" data-testid="hierarchy-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Organization</div>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Organization Hierarchy</h1>
        <p className="mt-1 text-sm text-slate-500">
          Reporting structure by hierarchy level with live task counts. Assignment permission flows downward
          unless same-level assignment is enabled in Settings.
        </p>
      </div>
      <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-8">
        <div className="mx-auto max-w-5xl space-y-2">
          {levels.map((level, li) => {
            const members = users.filter((u) => u.hierarchy_level === level)
              .sort((a, b) => (a.reporting_manager_id || "").localeCompare(b.reporting_manager_id || ""));
            return (
              <div key={level}>
                {li > 0 && (
                  <div className="flex justify-center py-1 text-slate-300">
                    <ChevronDown className="h-5 w-5" />
                  </div>
                )}
                <div className="mb-2 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                  Level {level}
                </div>
                <div className="flex flex-wrap justify-center gap-4">
                  {members.map((u) => (
                    <div key={u.id} className="flex flex-col items-center gap-1">
                      <UserCard user={u} isRoot={li === 0} />
                      {u.reporting_manager_id && byId[u.reporting_manager_id] && (
                        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-medium text-slate-500">
                          reports to {byId[u.reporting_manager_id].name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
